import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { TrashIcon, XMarkIcon } from "@heroicons/react/24/outline";

import { apiErrorMessage } from "../../services/api";
import {
  createReportSchedule,
  deleteReportSchedule,
  fetchReportSchedules,
  updateReportSchedule,
} from "../../services/reportService";
import { labelFor } from "../../services/enumLabels";
import { formatDate } from "../../services/datetime";

const FREQUENCIES = ["daily", "weekly", "monthly"];
// Scheduled mail carries Excel or CSV; PDF is a print page, not an attachment.
const FORMATS = ["xlsx", "csv"];
const WEEKDAYS = [0, 1, 2, 3, 4, 5, 6];

/**
 * Schedule this report by email to its creator (decision 11).
 *
 * The filters are frozen *relative* — "last 7 days", not 1–7 Sep — so the
 * period is picked here from the presets; branches, currency, comparison and
 * the report's own filters are taken from what is applied on screen. The
 * reader's existing schedules for this report are listed underneath.
 */
export default function ScheduleModal({ open, onClose, report, filters, periods = [], onSaved }) {
  const { t, i18n } = useTranslation();
  const [form, setForm] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const loadSchedules = useCallback(() => {
    if (!report) return;
    fetchReportSchedules(report.key)
      .then(setSchedules)
      .catch(() => setSchedules([]));
  }, [report]);

  useEffect(() => {
    if (!open) return;
    setForm({
      name: report?.title ?? "",
      frequency: "weekly",
      time: "08:00",
      day_of_week: 0,
      day_of_month: 1,
      format: "xlsx",
      period: filters.period && periods.includes(filters.period) ? filters.period : "last_7_days",
    });
    setError("");
    setFieldErrors({});
    loadSchedules();
  }, [open, report, filters.period, periods, loadSchedules]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape" && !saving) onClose();
    };
    document.addEventListener("keydown", onKey);

    return () => document.removeEventListener("keydown", onKey);
  }, [open, saving, onClose]);

  if (!open || !form) return null;

  const update = (patch) => setForm((current) => ({ ...current, ...patch }));

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    setFieldErrors({});

    // Relative only: from/to are never frozen into a schedule.
    const { from, to, period, ...rest } = filters;
    const frozen = Object.fromEntries(
      Object.entries({ ...rest, period: form.period }).filter(
        ([, value]) =>
          value !== "" &&
          value !== null &&
          value !== undefined &&
          !(Array.isArray(value) && value.length === 0)
      )
    );

    try {
      await createReportSchedule({
        name: form.name || null,
        report_key: report.key,
        filters: frozen,
        frequency: form.frequency,
        time: form.time,
        day_of_week: form.frequency === "weekly" ? Number(form.day_of_week) : null,
        day_of_month: form.frequency === "monthly" ? Number(form.day_of_month) : null,
        format: form.format,
        locale: i18n.language === "ar" ? "ar" : "en",
      });
      onSaved?.();
      loadSchedules();
      onClose();
    } catch (err) {
      setFieldErrors(err?.response?.data?.errors ?? {});
      setError(apiErrorMessage(err, t("reports.shell.schedule.failed")));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (schedule) => {
    try {
      await updateReportSchedule(schedule.id, { is_active: !schedule.is_active });
      loadSchedules();
    } catch (err) {
      setError(apiErrorMessage(err, t("reports.shell.schedule.failed")));
    }
  };

  const remove = async (schedule) => {
    try {
      await deleteReportSchedule(schedule.id);
      loadSchedules();
    } catch (err) {
      setError(apiErrorMessage(err, t("reports.shell.schedule.failed")));
    }
  };

  const fieldError = (name) => fieldErrors[name]?.[0] ?? fieldErrors[`filters.${name}`]?.[0];
  // 4 Jan 2026 was a Sunday (day 0).
  const weekday = (day) =>
    formatDate(`2026-01-0${4 + day}`, i18n.language, {
      weekday: "long",
      day: undefined,
      month: undefined,
      year: undefined,
    });

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4"
      onMouseDown={(e) => e.target === e.currentTarget && !saving && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="schedule-title"
        className="my-8 w-full max-w-lg rounded-xl bg-surface shadow-e2"
      >
        <form onSubmit={submit} noValidate>
          <header className="flex items-start justify-between gap-3 border-b border-line px-5 py-4">
            <div>
              <h2 id="schedule-title" className="text-card-title font-bold">
                {t("reports.shell.schedule.title")}
              </h2>
              <p className="text-dense text-ink-2">{t("reports.shell.schedule.subtitle")}</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label={t("common.close")}
              className="grid h-9 w-9 place-items-center rounded-full hover:bg-raised"
            >
              <XMarkIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          </header>

          <div className="space-y-4 px-5 py-4">
            {error && (
              <div className="rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-dense text-danger-text">
                {error}
              </div>
            )}

            <div>
              <label className="field-label" htmlFor="schedule-name">
                {t("reports.shell.schedule.name")}
              </label>
              <input
                id="schedule-name"
                className="field-input"
                maxLength={120}
                value={form.name}
                onChange={(e) => update({ name: e.target.value })}
              />
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="field-label" htmlFor="schedule-frequency">
                  {t("reports.shell.schedule.frequency")}
                </label>
                <select
                  id="schedule-frequency"
                  className="field-input"
                  value={form.frequency}
                  onChange={(e) => update({ frequency: e.target.value })}
                >
                  {FREQUENCIES.map((f) => (
                    <option key={f} value={f}>
                      {t(`reports.shell.schedule.frequencies.${f}`)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="field-label" htmlFor="schedule-time">
                  {t("reports.shell.schedule.time")}
                </label>
                <input
                  id="schedule-time"
                  type="time"
                  className="field-input"
                  value={form.time}
                  onChange={(e) => update({ time: e.target.value })}
                />
                {fieldError("time") && <p className="field-error">{fieldError("time")}</p>}
              </div>

              {form.frequency === "weekly" && (
                <div>
                  <label className="field-label" htmlFor="schedule-weekday">
                    {t("reports.shell.schedule.dayOfWeek")}
                  </label>
                  <select
                    id="schedule-weekday"
                    className="field-input"
                    value={form.day_of_week}
                    onChange={(e) => update({ day_of_week: e.target.value })}
                  >
                    {WEEKDAYS.map((d) => (
                      <option key={d} value={d}>
                        {weekday(d)}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {form.frequency === "monthly" && (
                <div>
                  <label className="field-label" htmlFor="schedule-monthday">
                    {t("reports.shell.schedule.dayOfMonth")}
                  </label>
                  <select
                    id="schedule-monthday"
                    className="field-input"
                    value={form.day_of_month}
                    onChange={(e) => update({ day_of_month: e.target.value })}
                  >
                    {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="field-label" htmlFor="schedule-period">
                  {t("reports.shell.schedule.period")}
                </label>
                <select
                  id="schedule-period"
                  className="field-input"
                  value={form.period}
                  onChange={(e) => update({ period: e.target.value })}
                >
                  {periods.map((p) => (
                    <option key={p} value={p}>
                      {t(`reports.periods.${p}`)}
                    </option>
                  ))}
                </select>
                {fieldError("period") && <p className="field-error">{fieldError("period")}</p>}
              </div>

              <div>
                <label className="field-label" htmlFor="schedule-format">
                  {t("reports.shell.schedule.format")}
                </label>
                <select
                  id="schedule-format"
                  className="field-input"
                  value={form.format}
                  onChange={(e) => update({ format: e.target.value })}
                >
                  {FORMATS.map((f) => (
                    <option key={f} value={f}>
                      {t(`reports.exportFormats.${f}`)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <p className="text-caption text-ink-2">{t("reports.shell.schedule.hint")}</p>

            {schedules.length > 0 && (
              <div>
                <p className="mb-2 text-dense font-semibold">{t("reports.shell.schedule.existing")}</p>
                <ul className="divide-y divide-line rounded-lg border border-line">
                  {schedules.map((schedule) => (
                    <li key={schedule.id} className="flex items-center gap-3 px-3 py-2 text-dense">
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-semibold">{schedule.name || report.title}</p>
                        <p className="text-caption text-ink-2">
                          {t(`reports.shell.schedule.frequencies.${schedule.frequency}`)} · {schedule.time} ·{" "}
                          {labelFor("report_period", schedule.filters?.period)} ·{" "}
                          {schedule.format?.toUpperCase()}
                          {schedule.last_status === "forbidden"
                            ? ` · ${t("reports.shell.schedule.forbidden")}`
                            : ""}
                        </p>
                      </div>
                      <button
                        type="button"
                        className="text-dense font-semibold text-accent-text"
                        onClick={() => toggleActive(schedule)}
                      >
                        {schedule.is_active
                          ? t("reports.shell.schedule.pause")
                          : t("reports.shell.schedule.resume")}
                      </button>
                      <button
                        type="button"
                        aria-label={t("reports.shell.schedule.delete")}
                        className="grid h-8 w-8 place-items-center rounded-full text-danger-text hover:bg-danger-wash"
                        onClick={() => remove(schedule)}
                      >
                        <TrashIcon className="h-4 w-4" aria-hidden="true" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <footer className="flex justify-end gap-2 border-t border-line px-5 py-3">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
              {t("common.cancel")}
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? t("reports.shell.schedule.saving") : t("reports.shell.schedule.save")}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}
